import React, { useEffect, useMemo, useState } from "react";
import Header from "../general/Header";
import Aside from "../general/Aside";
import Footer from "../general/Footer";
import axios from '../../axios/axiosconf';
import Loading from "../general/Loading";
import Unauthorized from "../general/Unauthorized";
import { Link } from "react-router-dom";
import { formatDateOnly } from '../superadmin/superadminDateUtils';
import { useClientPagination } from "./useClientPagination";
import ReportPaginationFooter from "./ReportPaginationFooter";
import {
    getFormaPagoTexto,
    camposProveedorBasicosDesdeRegistro,
    descargarExcelXlsx,
    descargarCsvReporte,
} from '../../../utils/reporteExportHelpers';

function tienePermiso(userdata) {
    if (userdata.permissions && Array.isArray(userdata.permissions)) {
        return userdata.permissions.some((p) => p.slug === 'ver_listado_documentos_soporte');
    }
    return userdata.up && userdata.up.includes('ver_listado_documentos_soporte');
}

function ReporteEgresos() {
    const [loggedIn, setLoggedIn] = useState(false);
    const [hasPermission, setHasPermission] = useState(false);
    const [client, setClient] = useState([]);
    const [loading, setLoading] = useState(false);
    const [egresos, setEgresos] = useState([]);
    const [usuarios, setUsuarios] = useState([]);

    const [fechaInicio, setFechaInicio] = useState('');
    const [fechaFin, setFechaFin] = useState('');
    const [usuarioFiltro, setUsuarioFiltro] = useState('');
    const [busquedaProveedor, setBusquedaProveedor] = useState('');
    const [estadoFiltro, setEstadoFiltro] = useState('');
    const [historicoCompleto, setHistoricoCompleto] = useState(false);

    useEffect(() => {
        if (!localStorage.getItem('userdata')) {
            setHasPermission(false);
            setLoggedIn(true);
            return;
        }
        const userdata = JSON.parse(localStorage.getItem('userdata'));
        if (!tienePermiso(userdata)) {
            setHasPermission(false);
            setLoggedIn(true);
            return;
        }
        setHasPermission(true);

        const hoy = new Date();
        const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        const finMes = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
        setFechaInicio(inicioMes.toISOString().split('T')[0]);
        setFechaFin(finMes.toISOString().split('T')[0]);

        axios.get('/get-client?uid=' + userdata.user_id)
            .then((r) => { if (r.status === 200) setClient(r.data); setLoggedIn(true); })
            .catch(() => setLoggedIn(true));

        axios.get('/users-by-client?uid=' + userdata.user_id)
            .then((r) => { if (r.status === 200) setUsuarios(r.data); })
            .catch(() => setUsuarios([]));
    }, []);

    useEffect(() => {
        if (loggedIn && (historicoCompleto || (fechaInicio && fechaFin))) {
            cargarReporte();
        }
    }, [loggedIn, historicoCompleto, fechaInicio, fechaFin, usuarioFiltro, estadoFiltro, busquedaProveedor]);

    const cargarReporte = () => {
        if (!historicoCompleto && (!fechaInicio || !fechaFin)) {
            alert('Seleccione fecha inicio y fecha fin, o active "Histórico completo".');
            return;
        }
        setLoading(true);
        const userdata = JSON.parse(localStorage.getItem('userdata'));
        const params = {
            uid: userdata.user_id,
            fecha_inicio: historicoCompleto ? '1990-01-01' : fechaInicio,
            fecha_fin: historicoCompleto ? '3000-12-31' : fechaFin,
            usuario_id: usuarioFiltro || null,
            busqueda_proveedor: busquedaProveedor || null,
            estado: estadoFiltro !== '' ? estadoFiltro : null,
        };

        axios.get('/egresos', { params })
            .then((response) => {
                setEgresos(Array.isArray(response.data) ? response.data : []);
                setLoading(false);
            })
            .catch((error) => {
                console.error(error);
                setEgresos([]);
                setLoading(false);
            });
    };

    const handleLimpiarFiltros = () => {
        const hoy = new Date();
        const inicioMes = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
        const finMes = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
        setFechaInicio(inicioMes.toISOString().split('T')[0]);
        setFechaFin(finMes.toISOString().split('T')[0]);
        setUsuarioFiltro('');
        setBusquedaProveedor('');
        setEstadoFiltro('');
        setHistoricoCompleto(false);
        setTimeout(() => cargarReporte(), 100);
    };

    const getEstadoTexto = (estado) => {
        switch (parseInt(estado, 10)) {
            case 0: return 'Generada';
            case 1: return 'Anulada';
            default: return 'Desconocido';
        }
    };

    const getDocSoporteTexto = (egreso) => {
        if (egreso.documento_soporte_prefijo && egreso.documento_soporte_numero) {
            return `${egreso.documento_soporte_prefijo}-${egreso.documento_soporte_numero}`;
        }
        return 'N/A';
    };

    const getNombreUsuario = (egreso) => {
        const nombre = [egreso.usuario_nombre, egreso.usuario_apellido].filter(Boolean).join(' ').trim();
        if (nombre) return nombre;
        const u = usuarios.find((x) => x.id === egreso.users_id);
        return u ? `${u.name || ''} ${u.lastname || ''}`.trim() : 'N/A';
    };

    const totalesResumen = useMemo(() => {
        let valor = 0;
        let activos = 0;
        let anulados = 0;
        egresos.forEach((e) => {
            valor += parseFloat(e.valor_total || 0);
            if (Number(e.estado) === 0) activos++;
            else if (Number(e.estado) === 1) anulados++;
        });
        return { conteo: egresos.length, valor, activos, anulados };
    }, [egresos]);

    const pagination = useClientPagination(egresos);

    const formatearMoneda = (valor) =>
        '$' + Number(valor || 0).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

    const filasExcel = () => egresos.map((e) => {
        const prov = camposProveedorBasicosDesdeRegistro(e);
        return {
            'N° Egreso': e.numero || '',
            'Fecha': formatDateOnly(e.fecha_creacion),
            'Proveedor': e.nombre_proveedor || '',
            'Tipo doc. proveedor': prov.tipoDocumento,
            'N° doc. proveedor': prov.numeroDocumento,
            'Usuario': getNombreUsuario(e),
            'Forma de pago': getFormaPagoTexto(e.forma_pago),
            'Valor total': parseFloat(e.valor_total || 0),
            'Estado': getEstadoTexto(e.estado),
            'Doc. soporte': getDocSoporteTexto(e),
        };
    });

    const exportarExcel = () => {
        if (egresos.length === 0) {
            alert('No hay datos para exportar');
            return;
        }
        descargarExcelXlsx(
            filasExcel(),
            'Egresos',
            `reporte_egresos_${new Date().toISOString().split('T')[0]}.xlsx`
        );
    };

    const exportarCSV = () => {
        if (egresos.length === 0) {
            alert('No hay datos para exportar');
            return;
        }
        descargarCsvReporte(filasExcel(), `reporte_egresos_${new Date().toISOString().split('T')[0]}.csv`);
    };

    if (hasPermission === false) return <Unauthorized />;
    if (!loggedIn) return <Loading />;

    return (
        <div className="hold-transition" style={{ fontSize: '13px' }}>
            <Header client={client} />
            <Aside client={client} activelink="reports" />
            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-12">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item"><Link to="/home">Home</Link></li>
                                    <li className="breadcrumb-item"><Link to="/estadisticas">Reportes</Link></li>
                                    <li className="breadcrumb-item"><Link to="/estadisticas-contables">Contables</Link></li>
                                    <li className="breadcrumb-item active">Egresos</li>
                                </ol>
                            </div>
                        </div>
                        <h1 className="m-0">Reporte de Egresos</h1>
                    </div>
                </div>

                <section className="content">
                    <div className="container-fluid">
                        <div className="card report-fin-card">
                            <div className="card-header"><h3 className="card-title">Filtros de búsqueda</h3></div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-2">
                                        <label>Fecha inicio</label>
                                        <input type="date" className="form-control form-control-sm" value={fechaInicio} disabled={historicoCompleto} onChange={(e) => setFechaInicio(e.target.value)} />
                                    </div>
                                    <div className="col-md-2">
                                        <label>Fecha fin</label>
                                        <input type="date" className="form-control form-control-sm" value={fechaFin} disabled={historicoCompleto} onChange={(e) => setFechaFin(e.target.value)} />
                                    </div>
                                    <div className="col-md-2">
                                        <label>Usuario</label>
                                        <select className="form-control form-control-sm" value={usuarioFiltro} onChange={(e) => setUsuarioFiltro(e.target.value)}>
                                            <option value="">Todos</option>
                                            {usuarios.map((u) => <option key={u.id} value={u.id}>{u.name} {u.lastname}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-2">
                                        <label>Estado</label>
                                        <select className="form-control form-control-sm" value={estadoFiltro} onChange={(e) => setEstadoFiltro(e.target.value)}>
                                            <option value="">Todos</option>
                                            <option value="0">Generada</option>
                                            <option value="1">Anulada</option>
                                        </select>
                                    </div>
                                    <div className="col-md-4">
                                        <label>Proveedor</label>
                                        <input type="text" className="form-control form-control-sm" placeholder="Buscar por nombre..." value={busquedaProveedor} onChange={(e) => setBusquedaProveedor(e.target.value)} />
                                    </div>
                                </div>
                                <div className="row mt-2">
                                    <div className="col-md-4">
                                        <div className="form-check mt-1">
                                            <input className="form-check-input" type="checkbox" id="historicoEgresos" checked={historicoCompleto} onChange={(e) => setHistoricoCompleto(e.target.checked)} />
                                            <label className="form-check-label" htmlFor="historicoEgresos">Histórico completo (sin límite de fechas)</label>
                                        </div>
                                    </div>
                                </div>
                                <div className="row mt-3">
                                    <div className="col-md-12">
                                        <button type="button" className="btn btn-primary btn-sm mr-2" onClick={cargarReporte} disabled={loading}>
                                            <i className="fas fa-search"></i> Buscar
                                            {loading && <span className="spinner-border spinner-border-sm ml-2"></span>}
                                        </button>
                                        <button type="button" className="btn btn-secondary btn-sm mr-2" onClick={handleLimpiarFiltros}>
                                            <i className="fas fa-eraser"></i> Limpiar filtros
                                        </button>
                                        <button type="button" className="btn btn-success btn-sm mr-2" onClick={exportarExcel} disabled={egresos.length === 0}>
                                            <i className="fas fa-file-excel"></i> Excel
                                        </button>
                                        <button type="button" className="btn btn-info btn-sm" onClick={exportarCSV} disabled={egresos.length === 0}>
                                            <i className="fas fa-file-csv"></i> CSV
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col-md-3">
                                <div className="card card-outline card-secondary">
                                    <div className="card-body">
                                        <h3 className="mb-0">{totalesResumen.conteo}</h3>
                                        <p className="text-muted mb-0 small">Egresos</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card card-outline card-info">
                                    <div className="card-body">
                                        <h3 className="mb-0">{formatearMoneda(totalesResumen.valor)}</h3>
                                        <p className="text-muted mb-0 small">Valor total</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card card-outline card-success">
                                    <div className="card-body">
                                        <h3 className="mb-0">{totalesResumen.activos}</h3>
                                        <p className="text-muted mb-0 small">Generadas</p>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card card-outline card-danger">
                                    <div className="card-body">
                                        <h3 className="mb-0">{totalesResumen.anulados}</h3>
                                        <p className="text-muted mb-0 small">Anuladas</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card report-fin-card">
                            <div className="card-header">
                                <h3 className="card-title">Resultados ({egresos.length} egresos)</h3>
                            </div>
                            <div className="card-body p-0">
                                {loading ? (
                                    <div className="text-center py-4"><div className="spinner-border" role="status"><span className="sr-only">Cargando...</span></div></div>
                                ) : (
                                    <div className="report-fin-table-wrap report-fin-table-wrap--fit">
                                        <table className="table table-bordered table-hover mb-0 report-fin-table">
                                            <thead>
                                                <tr>
                                                    <th>N° Egreso</th>
                                                    <th>Fecha</th>
                                                    <th>Proveedor</th>
                                                    <th className="text-center">Doc. soporte</th>
                                                    <th>Usuario</th>
                                                    <th className="text-center">Estado</th>
                                                    <th className="text-right">Valor total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {egresos.length > 0 ? pagination.paginatedItems.map((e) => (
                                                    <tr key={e.id}>
                                                        <td><strong>{e.numero}</strong></td>
                                                        <td>{formatDateOnly(e.fecha_creacion)}</td>
                                                        <td>{e.nombre_proveedor}</td>
                                                        <td className="text-center">{getDocSoporteTexto(e)}</td>
                                                        <td>{getNombreUsuario(e)}</td>
                                                        <td className="text-center">{getEstadoTexto(e.estado)}</td>
                                                        <td className="text-right"><strong>{formatearMoneda(e.valor_total)}</strong></td>
                                                    </tr>
                                                )) : (
                                                    <tr>
                                                        <td colSpan="7" className="text-center py-4 text-muted">No se encontraron egresos</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                {!loading && (
                                    <ReportPaginationFooter
                                        totalRegistros={pagination.totalRegistros}
                                        indicePrimerRegistro={pagination.indicePrimerRegistro}
                                        indiceUltimoRegistro={pagination.indiceUltimoRegistro}
                                        paginaActual={pagination.paginaActual}
                                        cambiarPagina={pagination.cambiarPagina}
                                        totalPaginas={pagination.totalPaginas}
                                        registrosPorPagina={pagination.registrosPorPagina}
                                        onPageSizeChange={pagination.handlePageSizeChange}
                                        itemLabel="egresos"
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </div>
            <Footer showToast={false} delay={0} />
        </div>
    );
}

export default ReporteEgresos;
